/* ==========================================================================
   Article Modal Component
   Full Scraped Content, Official Banner Graphics, YouTube Video Embeds & 100% i18n
   ========================================================================== */

import { getLocale, t } from '../locales/i18n.js';
import { getArticleBySlug } from '../services/articleStore.js';
import { articleIndexTranslations } from '../locales/articleTranslationsData.js';
import { articleFullTranslations } from '../locales/articleFullTranslations.js';
import { getSvgIcon } from './Icons.js';
import { createYouTubeEmbed } from './YouTubeLazyEmbed.js';
import { deobfuscateEmails } from '../utils/emailObfuscator.js';

function resolveArticleImg(imgUrl, cleanId = '', lang = '') {
  const caseStudyImages = {
    'sound-detection-improve-safety-cities': '/images/Studie_Pisek.png',
    'sed-kiss-integration-automated-alerts': (lang === 'cs' ? '/images/propojení SED a KISS_CZ.png' : '/images/SED_a_KISS.png'),
    'ai-gunshot-detection-norway': '/images/Norska_studie.png',
    'glass-break-detection-cameras': '/images/Studie_Tristeni_skla.png',
    'from-acoustic-mirrors-to-ai-sound-detection': '/images/Studie_akusticka_detekce.png',
    'security-systems-dont-just-have-to-see-they-can-hear-too': '/images/Studie_integrace.png',
    'workplace-aggression-detection': '/images/Studie_Agrese.png',
    'how-audio-analysis-improves-security-and-enables-early-threat-detection': '/images/Studie_Detekce zvukem.png',
    'drone-detection-why-drones-are-a-growing-security-threat-and-how-to-detect-them-early': '/images/Studie_Detekce_dron.png',
    'why-cctv-fails-sound-event-detection': '/images/Studie_UK.png',
    'case-study-university-campus-security': '/images/Studie_univerzity.png',
    'case-study-pilsen-acoustic-detection': '/images/Studie_Plzen.png',
    'how-5-seconds-can-change-everything-case-study': '/images/Studie_letiste.png',
    'a-key-to-martyns-law-compliance-enhancing-security-measures': '/images/Studie_Martins_Law.png',
    'monument-protection': '/images/Studie_Vandalismus.png',
    'student-safety-and-security': '/images/Studie_Studenti.png',
    'protection-external-borders': '/images/Studie_ochrana_hranic.png',
    'drone-detection-in-prisons': '/images/Studie_drony_veznice.png',
    'protecting-employee-safety-in-risky-places': '/images/Studie_zamestnanci.png',
    'how-does-sed-help-municipal-police': '/images/Studie_policie.png'
  };

  if (caseStudyImages[cleanId]) {
    return typeof caseStudyImages[cleanId] === 'function' ? caseStudyImages[cleanId](lang) : caseStudyImages[cleanId];
  }

  if (cleanId === 'sed-camera-version' || cleanId === 'edge-solutions-architecture' || cleanId === 'edge') {
    return '/images/edge-reseni-detail.png';
  }
  if (cleanId === 'centralized-server-solution' || cleanId === 'server-solution' || cleanId === 'server') {
    return '/images/serverove-reseni-detail.png';
  }
  if (cleanId === 'sed-mobile-application' || cleanId === 'mobile-app' || cleanId === 'mobile') {
    return '/images/mobilni-reseni-detail.png';
  }
  if (!imgUrl) return '';
  if (imgUrl.startsWith('/')) return imgUrl;
  const fileName = imgUrl.split('/').pop().split('?')[0];
  if (fileName.includes('Curiosity-Lab')) return '/images/Curiosity-Lab-in-Atlanta-Tv-news-EN-title-1024x576.png';
  if (fileName.includes('Lukas-Svoboda')) return '/images/Lukas-Svoboda_Mlada-Fronta-1-1024x576.png';
  if (fileName.includes('CT-Udalosti')) return '/images/CT-Udalosti-EN-1-1024x576.png';
  if (fileName.includes('Muze-detekce')) return '/images/Muze-detekce-zvuku-zvysit-bezpecnost-ve-mestech_EN-2048x1152.png';
  if (fileName.includes('Drony')) return '/images/Drony_EN-1024x576.png';
  if (fileName.includes('SED_Novinky')) return '/images/SED_Novinky-aplikace_EN-2.png';
  if (fileName.includes('Plzen')) return '/images/Plzen_sit_CB_RGB_pozitiv-1024x168.png';
  return imgUrl;
}


const modalUi = {
  en: {
    docLabel: 'Official Technical Document • JALUD Embedded s.r.o.',
    closeBtn: 'Close Article',
    relatedTechEyebrow: 'RELATED SED TECHNOLOGY & SOLUTIONS',
    relatedArticlesEyebrow: '📚 RELATED CASE STUDIES & ARTICLES',
    relatedCountBadge: (n) => `${n} Related Materials`,
    exploreBtn: 'Explore Case Study →',
    shareTitle: 'Share Article',
    copyLinkBtn: 'Copy Link',
    copiedNotice: 'Link copied to clipboard!',
    shareNativeBtn: 'Share ↗'
  },
  cs: {
    docLabel: 'Oficiální technický dokument • JALUD Embedded s.r.o.',
    closeBtn: 'Zavřít článek',
    relatedTechEyebrow: 'SOUVISEJÍCÍ TECHNOLOGIE A ŘEŠENÍ SED',
    relatedArticlesEyebrow: '📚 SOUVISEJÍCÍ PŘÍPADOVÉ STUDIE A ČLÁNKY',
    relatedCountBadge: (n) => `${n} související materiály`,
    exploreBtn: 'Číst studii →',
    shareTitle: 'Sdílet článek',
    copyLinkBtn: 'Kopírovat odkaz',
    copiedNotice: 'Odkaz byl zkopírován do schránky!',
    shareNativeBtn: 'Sdílet ↗'
  },
  de: {
    docLabel: 'Offizielles technisches Dokument • JALUD Embedded s.r.o.',
    closeBtn: 'Artikel schließen',
    relatedTechEyebrow: 'VERWANDTE SED-TECHNOLOGIE & LÖSUNGEN',
    relatedArticlesEyebrow: '📚 VERWANDTE FALLSTUDIEN & ARTIKEL',
    exploreBtn: 'Fallstudie lesen →',
    shareTitle: 'Artikel teilen',
    copyLinkBtn: 'Link kopieren',
    copiedNotice: 'Link in die Zwischenablage kopiert!',
    shareNativeBtn: 'Teilen ↗'
  },
  es: {
    docLabel: 'Documento Técnico Oficial • JALUD Embedded s.r.o.',
    closeBtn: 'Cerrar artículo',
    relatedTechEyebrow: 'TECNOLOGÍA Y SOLUCIONES SED RELACIONADAS',
    relatedArticlesEyebrow: '📚 CASOS DE ESTUDIO Y ARTÍCULOS RELACIONADOS',
    exploreBtn: 'Explorar caso de estudio →',
    shareTitle: 'Compartir artículo',
    copyLinkBtn: 'Copiar enlace',
    copiedNotice: '¡Enlace copiado al portapapeles!',
    shareNativeBtn: 'Compartir ↗'
  },
  it: {
    docLabel: 'Documento Tecnico Ufficiale • JALUD Embedded s.r.o.',
    closeBtn: 'Chiudi articolo',
    relatedTechEyebrow: 'TECNOLOGIE E SOLUZIONI SED CORRELATE',
    relatedArticlesEyebrow: '📚 CASI DI STUDIO E ARTICOLI CORRELATI',
    exploreBtn: 'Esplora caso di studio →',
    shareTitle: 'Condividi articolo',
    copyLinkBtn: 'Copia link',
    copiedNotice: 'Link copiato negli appunti!',
    shareNativeBtn: 'Condividi ↗'
  },
  pt: {
    docLabel: 'Documento Técnico Oficial • JALUD Embedded s.r.o.',
    closeBtn: 'Fechar artigo',
    relatedTechEyebrow: 'TECNOLOGIA E SOLUÇÕES SED RELACIONADAS',
    relatedArticlesEyebrow: '📚 ESTUDOS DE CASO E ARTIGOS RELACIONADOS',
    exploreBtn: 'Explorar estudo de caso →',
    shareTitle: 'Compartilhar artigo',
    copyLinkBtn: 'Copiar link',
    copiedNotice: 'Link copiado para a área de transferência!',
    shareNativeBtn: 'Compartilhar ↗'
  },
  ar: {
    docLabel: 'وثيقة تقنية رسمية • JALUD Embedded s.r.o.',
    closeBtn: 'إغلاق المقال',
    relatedTechEyebrow: 'تقنيات وحلول SED ذات الصلة',
    relatedArticlesEyebrow: '📚 دراسات الحالة والمقالات ذات الصلة',
    exploreBtn: 'استكشف دراسة الحالة ←',
    shareTitle: 'مشاركة المقال',
    copyLinkBtn: 'نسخ الرابط',
    copiedNotice: 'تم نسخ الرابط إلى الحافظة!',
    shareNativeBtn: 'مشاركة ↗'
  }
};

let activeEscapeHandler = null;
let lastFocusedElement = null;

export function openArticleModal(articleId) {
  let modalContainer = document.getElementById('sed-article-modal-root');
  if (!modalContainer) {
    modalContainer = document.createElement('div');
    modalContainer.id = 'sed-article-modal-root';
    document.body.appendChild(modalContainer);
  }

  if (!lastFocusedElement && document.activeElement) {
    lastFocusedElement = document.activeElement;
  }
  if (activeEscapeHandler) {
    window.removeEventListener('keydown', activeEscapeHandler);
    activeEscapeHandler = null;
  }

  const lang = getLocale() || 'en';

  // Find exact match or slug match
  const cleanId = (articleId || '').replace('#', '').replace('/case-studies/', '').replace('/latest-news/', '').replace('/product-updates/', '').replace('/news/', '').replace('/detection/', '').replace(/\/$/, '') || articleId;
  
  const rawArticle = getArticleBySlug(cleanId, lang) || getArticleBySlug(articleId, lang) || {
    title: 'Sound Event Detector Official Report',
    publisher: 'JALUD Embedded Official Report',
    date: 'Official Document',
    img: '/images/2.png',
    yt: '',
    content: `<p style="line-height: 1.75;">Detailed report and technical analytics for Sound Event Detector deployment.</p>`
  };

  const displayTitle = rawArticle.title || rawArticle.slug;
  const displayExcerpt = rawArticle.excerpt || '';
  const displayCategory = rawArticle.categoryDisplay || rawArticle.publisher || (rawArticle.category || '').replace('-', ' ').toUpperCase();
  const displayContent = rawArticle.content;

  const ui = modalUi[lang] || modalUi.en;

  // Determine category path for canonical permalink
  const rawCat = (rawArticle.category || 'latest-news').toLowerCase();
  const catPath = rawCat.includes('case') ? 'case-studies' : (rawCat.includes('product') ? 'product-updates' : 'news');
  const canonicalUrl = `https://www.jalud.eu/${catPath}/${cleanId}`;

  // Update URL hash for direct browser bookmarking and sharing
  try {
    if (window.location.hash !== `#${catPath}/${cleanId}`) {
      window.history.replaceState(null, '', `/#${catPath}/${cleanId}`);
    }
  } catch (e) {}

  modalContainer.innerHTML = `
    <div class="sed-modal-backdrop" id="sed-modal-bg" style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(10, 25, 47, 0.75); backdrop-filter: blur(6px); z-index: 99999; display: flex; align-items: center; justify-content: center; padding: 1rem;">
      <div class="sed-modal-box" style="max-width: 840px; width: 100%; max-height: 88vh; overflow-y: auto; background: #ffffff; border-radius: 20px; box-shadow: 0 25px 60px rgba(0,0,0,0.4); padding: 2.25rem; position: relative;">
        
        <button id="sed-modal-close" style="position: absolute; top: 20px; right: 20px; background: rgba(0,0,0,0.06); border: none; width: 38px; height: 38px; border-radius: 50%; font-size: 1.2rem; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.2s; color: var(--navy-dark);" title="Close Modal">✕</button>

        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; flex-wrap: wrap; gap: 0.5rem; padding-right: 40px;">
          <div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
            <span style="font-size: 0.8rem; font-weight: 800; color: var(--blue-primary); text-transform: uppercase; letter-spacing: 0.6px;">
              ${displayCategory || rawArticle.publisher || 'JALUD Embedded • Sound Event Detector'}
            </span>
            ${rawArticle.date ? `
              <span style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); background: #f1f5f9; padding: 2px 8px; border-radius: 4px; border: 1px solid var(--slate-line);">
                📅 ${rawArticle.date}
              </span>
            ` : ''}
          </div>
        </div>

        <h2 class="sed-modal-title" style="font-family: var(--font-heading); color: var(--navy-dark); margin-bottom: 1.25rem;">
          ${displayTitle}
        </h2>

        ${displayExcerpt ? `
          <div style="background: var(--blue-ultra-light); border-left: 4px solid var(--blue-primary); padding: 1rem 1.25rem; border-radius: 0 8px 8px 0; margin-bottom: 1.5rem; font-size: 1.02rem; color: var(--navy-dark); font-weight: 600; line-height: 1.6;">
            ${displayExcerpt}
          </div>
        ` : ''}

        ${(() => {
          let mediaHtml = '';
          const rawVideos = rawArticle.videos || (rawArticle.yt ? [rawArticle.yt] : []);
          const videoList = Array.isArray(rawVideos) ? rawVideos : [rawVideos];

          if (videoList.length > 0) {
            videoList.forEach((v, idx) => {
              if (!v) return;
              mediaHtml += `
                <div id="modal-video-slot-${idx}" style="margin-bottom: 1.75rem;"></div>
              `;
            });
          }

          const resolvedImg = resolveArticleImg(rawArticle.img, cleanId, lang);
          if (resolvedImg) {
            mediaHtml = `
              <div style="border-radius: 14px; overflow: hidden; margin-bottom: 1.75rem; max-height: 420px; background-color: var(--navy-dark); text-align: center;">
                <img src="${resolvedImg}" alt="${displayTitle}" style="width: 100%; height: 100%; object-fit: cover; display: block;">
              </div>
            ` + mediaHtml;
          }
          return mediaHtml;
        })()}

        <div style="font-size: 1.05rem; color: var(--text-dark); line-height: 1.8;">
          ${displayContent}
        </div>

        ${(() => {
          let html = '';
          const explicitCross = rawArticle.crossReferences || [];
          let links = [];

          if (explicitCross.length > 0) {
            links = explicitCross.map(c => `<a href="${c.url || '#solutions'}" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="${c.slug || (c.url || '').replace('#', '')}"><span>${c.icon || '🔗'}</span> <span>${c.title}</span> →</a>`);
          } else {
            const fullText = ((rawArticle.title || '') + ' ' + (rawArticle.content || '')).toLowerCase();
            if (fullText.includes('drone') || fullText.includes('uav')) {
              links.push(`<a href="#detection-drone" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="detection-drone">${getSvgIcon('drone', 16, 'var(--blue-primary)')} ${t('detectionTypes.drone')} →</a>`);
            }
            if (fullText.includes('gunshot') || fullText.includes('shooting') || fullText.includes('firearm')) {
              links.push(`<a href="#detection-gunshot" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="detection-gunshot">${getSvgIcon('gunshot', 16, 'var(--blue-primary)')} ${t('detectionTypes.gunshot')} →</a>`);
            }
            if (fullText.includes('glass')) {
              links.push(`<a href="#detection-glass" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="detection-glass">${getSvgIcon('glass', 16, 'var(--blue-primary)')} ${t('detectionTypes.glass')} →</a>`);
            }
            if (fullText.includes('scream') || fullText.includes('aggression') || fullText.includes('panic')) {
              links.push(`<a href="#detection-scream" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="detection-scream">${getSvgIcon('scream', 16, 'var(--blue-primary)')} ${t('detectionTypes.scream')} →</a>`);
            }
            if (fullText.includes('graffiti') || fullText.includes('spray')) {
              links.push(`<a href="#detection-graffiti" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="detection-graffiti">${getSvgIcon('graffiti', 16, 'var(--blue-primary)')} ${t('detectionTypes.graffiti')} →</a>`);
            }
            if (fullText.includes('grinder') || fullText.includes('drill')) {
              links.push(`<a href="#detection-grinder" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="detection-grinder">${getSvgIcon('grinder', 16, 'var(--blue-primary)')} ${t('detectionTypes.grinder')} →</a>`);
            }
            if (fullText.includes('distributor') || fullText.includes('integrator') || fullText.includes('partner')) {
              links.push(`<a href="#distributors-map" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="distributors-map">${getSvgIcon('coopMap', 16, 'var(--blue-primary)')} ${t('nav.distributorsMap')} →</a>`);
            }
            if (fullText.includes('hospital') || fullText.includes('healthcare')) {
              links.push(`<a href="#market-healthcare" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="market-healthcare">${getSvgIcon('marketHealthcare', 16, 'var(--blue-primary)')} ${t('marketTypes.healthcare')} →</a>`);
            }
            if (fullText.includes('pilsen') || fullText.includes('city') || fullText.includes('police')) {
              links.push(`<a href="#market-public" style="color: var(--blue-primary); font-weight: 700; text-decoration: underline; display: inline-flex; align-items: center; gap: 6px;" data-modal-link="market-public">${getSvgIcon('marketPublic', 16, 'var(--blue-primary)')} ${t('marketTypes.public')} →</a>`);
            }
          }

          if (links.length > 0) {
            html += `
              <div style="background-color: var(--blue-light); border: 1px solid var(--blue-primary); border-radius: 12px; padding: 1.25rem; margin-top: 2rem;">
                <div style="font-size: 0.8rem; font-weight: 800; color: var(--navy-dark); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.5rem;">${ui.relatedTechEyebrow}</div>
                <div style="display: flex; flex-direction: column; gap: 0.5rem; font-size: 0.95rem;">
                  ${links.join('')}
                </div>
              </div>
            `;
          }

          const relatedList = rawArticle.relatedArticles || [];
          if (relatedList.length > 0) {
            html += `
              <div style="background-color: #f8fafc; border: 1.5px solid var(--slate-line); border-radius: 14px; padding: 1.5rem; margin-top: 1.75rem;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.5rem;">
                  <div style="font-size: 0.82rem; font-weight: 800; color: var(--navy-dark); text-transform: uppercase; letter-spacing: 0.6px; display: flex; align-items: center; gap: 6px;">
                    ${ui.relatedArticlesEyebrow}
                  </div>
                  <span style="font-size: 0.74rem; font-weight: 700; color: var(--text-muted); background: #ffffff; border: 1px solid var(--slate-line); padding: 2px 8px; border-radius: 12px;">
                    ${ui.relatedCountBadge ? ui.relatedCountBadge(relatedList.length) : `${relatedList.length} související materiály`}
                  </span>
                </div>
                <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 1rem;">
                  ${relatedList.map(rel => {
                    const rArt = getArticleBySlug(rel.slug, lang);
                    const rTitle = (rArt && rArt.title) || rel.title || rel.slug;
                    const rCat = (rArt && rArt.categoryDisplay) || (rel.category || 'case-studies').replace('-', ' ');
                    const rImg = (rArt && rArt.img) || rel.img;

                    return `
                      <div class="sed-card sed-related-card" data-article-id="${rel.slug}" style="padding: 0; overflow: hidden; cursor: pointer; background: #ffffff; border: 1px solid var(--slate-line); border-radius: 10px; display: flex; flex-direction: column; justify-content: space-between; box-shadow: 0 2px 8px rgba(0,0,0,0.03); transition: transform 0.2s, box-shadow 0.2s;" onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 16px rgba(0,0,0,0.08)';" onmouseout="this.style.transform='none'; this.style.boxShadow='0 2px 8px rgba(0,0,0,0.03)';">
                        <div>
                          <div style="height: 110px; overflow: hidden; background: var(--navy-dark); position: relative;">
                            <img src="${resolveArticleImg(rImg) || '/images/2.png'}" alt="${rTitle}" style="width: 100%; height: 100%; object-fit: cover;">
                            <div style="position: absolute; top: 6px; left: 6px; background: rgba(53, 168, 223, 0.95); color: #ffffff; padding: 2px 6px; border-radius: 4px; font-size: 0.65rem; font-weight: 800; text-transform: uppercase;">
                              ${rCat}
                            </div>
                            ${rel.date ? `
                              <div style="position: absolute; bottom: 6px; right: 6px; background: rgba(10, 25, 47, 0.85); color: #ffffff; padding: 1px 5px; border-radius: 3px; font-size: 0.65rem; font-weight: 700;">
                                ${rel.date}
                              </div>
                            ` : ''}
                          </div>
                          <div style="padding: 0.85rem 0.85rem 0.5rem;">
                            <h4 style="font-size: 0.9rem; font-weight: 700; color: var(--navy-dark); margin: 0 0 0.35rem 0; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                              ${rTitle}
                            </h4>
                          </div>
                        </div>
                        <div style="padding: 0 0.85rem 0.75rem;">
                          <button class="sed-btn sed-btn-primary" style="width: 100%; font-size: 0.75rem; padding: 4px 8px;">${ui.exploreBtn}</button>
                        </div>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `;
          }

          return html;
        })()}

        <!-- Direct Share & Citations Toolbar -->
        <div style="margin: 2.25rem 0 1rem; padding: 1.25rem 1.5rem; background: var(--blue-ultra-light); border: 1px solid var(--blue-light); border-radius: 14px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem;">
          <div>
            <div style="font-weight: 800; font-size: 0.95rem; color: var(--navy-dark); display: flex; align-items: center; gap: 6px;">
              <span>📤</span> <span>${ui.shareTitle}</span>
            </div>
            <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">
              ${canonicalUrl}
            </div>
          </div>
          
          <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
            <button id="sed-copy-permalink-btn" class="sed-btn sed-btn-primary" style="font-size: 0.82rem; padding: 0.45rem 0.9rem; display: inline-flex; align-items: center; gap: 6px;" title="Copy direct permalink">
              <span>🔗</span> <span id="sed-copy-text">${ui.copyLinkBtn}</span>
            </button>
            <a href="https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.82rem; padding: 0.45rem 0.75rem; color: var(--navy-dark); border-color: var(--slate-line); text-decoration: none;" title="Share on LinkedIn">
              LinkedIn
            </a>
            <a href="https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalUrl)}&text=${encodeURIComponent(displayTitle)}" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.82rem; padding: 0.45rem 0.75rem; color: var(--navy-dark); border-color: var(--slate-line); text-decoration: none;" title="Share on X / Twitter">
              X
            </a>
            <a href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.82rem; padding: 0.45rem 0.75rem; color: var(--navy-dark); border-color: var(--slate-line); text-decoration: none;" title="Share on Facebook">
              Facebook
            </a>
            <a href="https://api.whatsapp.com/send?text=${encodeURIComponent(displayTitle + ' ' + canonicalUrl)}" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.82rem; padding: 0.45rem 0.75rem; color: var(--navy-dark); border-color: var(--slate-line); text-decoration: none;" title="Share on WhatsApp">
              WhatsApp
            </a>
            <button id="sed-native-share-btn" class="sed-btn sed-btn-outline" style="font-size: 0.82rem; padding: 0.45rem 0.75rem; color: var(--navy-dark); border-color: var(--slate-line); display: none;" title="Native Share">
              ${ui.shareNativeBtn}
            </button>
          </div>
        </div>

        <div style="border-top: 1px solid var(--slate-line); margin-top: 1.5rem; padding-top: 1.25rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;">
          <div style="font-size: 0.82rem; color: var(--text-light); font-weight: 600;">
            ${ui.docLabel}
          </div>
          <button id="sed-modal-close-btn" class="sed-btn sed-btn-primary" style="padding: 0.65rem 1.5rem; font-size: 0.9rem;">${ui.closeBtn}</button>
        </div>

      </div>
    </div>
  `;

  document.body.style.overflow = 'hidden';

  const modalBox = modalContainer.querySelector('.sed-modal-box');
  const backdrop = modalContainer.querySelector('#sed-modal-bg');
  const closeBtnX = modalContainer.querySelector('#sed-modal-close');
  const closeBtn = modalContainer.querySelector('#sed-modal-close-btn');

  // Mount Lazy & Privacy-Compliant YouTube Embeds
  const rawVideos = rawArticle.videos || (rawArticle.yt ? [rawArticle.yt] : []);
  const videoList = Array.isArray(rawVideos) ? rawVideos : [rawVideos];
  if (videoList.length > 0) {
    videoList.forEach((v, idx) => {
      const slot = modalContainer.querySelector(`#modal-video-slot-${idx}`);
      if (slot && v) {
        slot.appendChild(createYouTubeEmbed({
          videoId: v,
          title: `${displayTitle} - Video ${idx + 1}`,
          borderRadius: '14px'
        }));
      }
    });
  }

  // Mount Inline Video Embeds within article content body
  modalContainer.querySelectorAll('.sed-inline-video-slot').forEach(slot => {
    const vid = slot.getAttribute('data-yt');
    const vTitle = slot.getAttribute('data-title') || displayTitle;
    if (vid && !slot.hasChildNodes()) {
      slot.appendChild(createYouTubeEmbed({
        videoId: vid,
        title: vTitle,
        borderRadius: '14px'
      }));
    }
  });

  const copyBtn = modalContainer.querySelector('#sed-copy-permalink-btn');
  const copyText = modalContainer.querySelector('#sed-copy-text');
  const nativeShareBtn = modalContainer.querySelector('#sed-native-share-btn');

  if (copyBtn && copyText) {
    copyBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(canonicalUrl).then(() => {
        copyText.innerText = ui.copiedNotice;
        copyBtn.style.background = '#10b981';
        setTimeout(() => {
          copyText.innerText = ui.copyLinkBtn;
          copyBtn.style.background = '';
        }, 2500);
      }).catch(() => {
        prompt('Copy direct link:', canonicalUrl);
      });
    });
  }

  if (nativeShareBtn && navigator.share) {
    nativeShareBtn.style.display = 'inline-flex';
    nativeShareBtn.addEventListener('click', () => {
      navigator.share({
        title: displayTitle,
        text: displayExcerpt,
        url: canonicalUrl
      }).catch(() => {});
    });
  }

  // Deobfuscate all protected emails inside article content
  deobfuscateEmails(modalContainer);

  const closeModal = () => {
    if (activeEscapeHandler) {
      window.removeEventListener('keydown', activeEscapeHandler);
      activeEscapeHandler = null;
    }
    modalContainer.innerHTML = '';
    document.body.style.overflow = '';
    try {
      window.history.replaceState(null, '', `/#${catPath}`);
    } catch (e) {}
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      try {
        lastFocusedElement.focus();
      } catch (err) {}
      lastFocusedElement = null;
    }
  };

  activeEscapeHandler = (e) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeModal();
    }
  };
  window.addEventListener('keydown', activeEscapeHandler);

  if (closeBtnX) closeBtnX.addEventListener('click', closeModal);
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (backdrop) {
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) closeModal();
    });
  }

  // Intercept all related article clicks
  modalContainer.querySelectorAll('.sed-related-card, [data-article-id], [data-article-link], .sed-modal-internal-link').forEach(el => {
    el.addEventListener('click', (e) => {
      const artId = el.getAttribute('data-article-id') || el.getAttribute('data-article-link');
      if (artId) {
        e.preventDefault();
        openArticleModal(artId);
        if (modalBox) modalBox.scrollTop = 0;
      }
    });
  });

  // Intercept any <a> tag in modal content
  modalContainer.querySelectorAll('.sed-modal-box a').forEach(link => {
    const href = link.getAttribute('href') || '';
    if (href.startsWith('#')) {
      const targetHash = href.replace(/^#/, '');
      const art = getArticleBySlug(targetHash, lang);
      if (art) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          openArticleModal(targetHash);
          if (modalBox) modalBox.scrollTop = 0;
        });
      } else {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          closeModal();
          window.location.hash = href;
        });
      }
    }
  });

  modalContainer.querySelectorAll('[data-modal-link]').forEach(navLink => {
    navLink.addEventListener('click', (e) => {
      e.preventDefault();
      const targetHash = navLink.getAttribute('data-modal-link');
      closeModal();
      window.location.hash = targetHash;
    });
  });
}

export function renderArticleModal() {
  const modalRoot = document.createElement('div');
  modalRoot.id = 'sed-article-modal-root';
  return modalRoot;
}
